import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, FileText, CheckCircle2, AlertTriangle, Lock } from "lucide-react";
import api, { API } from "../lib/api";
import SEO from "../components/SEO";

const fmtDate = (s) => {
    if (!s) return "—";
    try { return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
    catch { return s; }
};
const fmtMoney = (n) => `$${(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const SpendDetail = () => {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(false);

    useEffect(() => {
        api.get(`/ledger/public/spend/${id}`)
            .then((r) => setData(r.data))
            .catch(() => setErr(true));
    }, [id]);

    if (err) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-20 text-center" data-testid="spend-detail-notfound">
                <h1 className="font-heading text-3xl text-[#1C252E] font-medium mb-3">Payout not found</h1>
                <p className="text-[#4A5568]">It may not be paid yet, or the link is invalid.</p>
                <Link to="/ledger" className="inline-flex items-center gap-2 mt-6 text-sm font-medium text-[#E53935] hover:underline">
                    <ArrowLeft className="w-4 h-4" /> Back to ledger
                </Link>
            </div>
        );
    }
    if (!data) return <div className="p-20 text-center text-[#4A5568]" data-testid="spend-detail-loading">Loading…</div>;

    return (
        <div data-testid="spend-detail-page" className="max-w-4xl mx-auto px-6 sm:px-8 py-12 lg:py-16">
            <SEO
                title={`${data.title} · Funded by Reddog Foundation`}
                description={`${data.purpose_public || data.title} · ${fmtMoney(data.amount)} paid to ${data.vendor_name || "vendor"}.`}
                path={`/ledger/spend/${id}`}
                image={data.impact_published || (data.after_photos && data.completion_notes) ? `${API}/impact/stories/${id}/og.png` : undefined}
            />
            <Link to="/ledger" className="inline-flex items-center gap-2 text-sm font-medium text-[#4A5568] hover:text-[#E53935] mb-8">
                <ArrowLeft className="w-4 h-4" /> Back to ledger
            </Link>

            <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3 flex items-center gap-2">
                <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2} />
                Paid out · {fmtDate(data.paid_at)}
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl text-[#1C252E] font-medium tracking-tight leading-[1.05] mb-4" data-testid="spend-detail-title">
                {data.title}
            </h1>
            <p className="text-lg text-[#4A5568] leading-relaxed mb-8" data-testid="spend-detail-purpose">
                {data.purpose_public}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
                <div className="bg-white border border-black/5 rounded-2xl p-5">
                    <p className="text-xs uppercase tracking-wider font-bold text-[#4A5568] mb-1">Amount</p>
                    <p className="font-heading text-2xl text-[#1C252E] font-medium" data-testid="spend-detail-amount">{fmtMoney(data.amount)}</p>
                </div>
                <div className="bg-white border border-black/5 rounded-2xl p-5">
                    <p className="text-xs uppercase tracking-wider font-bold text-[#4A5568] mb-1">Paid to</p>
                    <p className="font-heading text-base text-[#1C252E] font-medium">{data.vendor}</p>
                    <p className="text-xs text-[#4A5568] mt-1">Requested by {data.requested_by}</p>
                </div>
                <div className="bg-white border border-black/5 rounded-2xl p-5">
                    <p className="text-xs uppercase tracking-wider font-bold text-[#4A5568] mb-1">Fundraiser</p>
                    <p className="font-heading text-base text-[#1C252E] font-medium">{data.campaign?.name || "—"}</p>
                    {data.campaign?.restricted_fund && (
                        <p className="text-xs text-[#E53935] mt-1 inline-flex items-center gap-1">
                            <Lock className="w-3 h-3" strokeWidth={2} /> Restricted: {data.campaign.restricted_purpose || "see purpose"}
                        </p>
                    )}
                </div>
            </div>

            {/* Approval record */}
            <section className="bg-white border border-black/5 rounded-3xl p-6 sm:p-8 mb-8" data-testid="spend-detail-approvals">
                <h2 className="font-heading text-2xl text-[#1C252E] font-medium mb-4 flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-green-600" strokeWidth={2} />
                    Board approval record
                </h2>
                <ul className="space-y-3">
                    {data.approvers.map((a, i) => (
                        <li key={i} data-testid={`spend-detail-approver-${i}`} className="flex items-start gap-3 p-3 rounded-xl bg-[#F4F1EA]">
                            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
                            <div className="min-w-0 flex-1">
                                <p className="font-medium text-[#1C252E] text-sm">
                                    {a.role === "board" ? "Board member" : "Foundation admin"} · {a.email_masked}
                                </p>
                                <p className="text-xs text-[#4A5568]">Signed off {fmtDate(a.at)}</p>
                                {a.conflict_disclosed && (
                                    <p className="text-xs text-orange-700 mt-1 inline-flex items-start gap-1">
                                        <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                                        Conflict disclosed: {a.disclosure_notes || "(no notes)"}
                                    </p>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
                <p className="text-xs text-[#4A5568] mt-4 italic">
                    Approver emails are masked to protect privacy while still proving dual sign-off.
                </p>
            </section>

            {/* Before / after photos */}
            {(data.before_photos.length > 0 || data.after_photos.length > 0) && (
                <section className="bg-white border border-black/5 rounded-3xl p-6 sm:p-8 mb-8" data-testid="spend-detail-photos">
                    <h2 className="font-heading text-2xl text-[#1C252E] font-medium mb-4">Before · After</h2>
                    {data.before_photos.length > 0 && (
                        <div className="mb-6">
                            <p className="text-xs uppercase tracking-wider font-bold text-[#4A5568] mb-2">Before</p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {data.before_photos.map((url, i) => (
                                    <a key={i} href={url} target="_blank" rel="noreferrer">
                                        <img src={url} alt={`before ${i + 1}`} className="aspect-square w-full object-cover rounded-xl border border-black/5 hover:opacity-90 transition-opacity" />
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                    {data.after_photos.length > 0 && (
                        <div>
                            <p className="text-xs uppercase tracking-wider font-bold text-green-700 mb-2">After</p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {data.after_photos.map((url, i) => (
                                    <a key={i} href={url} target="_blank" rel="noreferrer">
                                        <img src={url} alt={`after ${i + 1}`} className="aspect-square w-full object-cover rounded-xl border border-black/5 hover:opacity-90 transition-opacity" />
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                </section>
            )}

            {/* Completion proof */}
            {(data.completion_notes || data.completion_proof_url) && (
                <section className="bg-green-50 border border-green-200 rounded-3xl p-6 sm:p-8 mb-8" data-testid="spend-detail-completion">
                    <h2 className="font-heading text-2xl text-[#1C252E] font-medium mb-3 flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600" strokeWidth={2} />
                        Completion proof
                    </h2>
                    {data.completion_notes && <p className="text-[#4A5568] leading-relaxed whitespace-pre-wrap">{data.completion_notes}</p>}
                    {data.completion_proof_url && (
                        <a href={data.completion_proof_url} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-[#E53935] hover:underline">
                            <FileText className="w-4 h-4" /> View completion document
                        </a>
                    )}
                </section>
            )}

            {/* Invoice summary */}
            {data.invoice_summary_url && (
                <section className="bg-white border border-black/5 rounded-3xl p-6 sm:p-8 mb-8" data-testid="spend-detail-invoice">
                    <h2 className="font-heading text-xl text-[#1C252E] font-medium mb-3">Invoice / receipt summary</h2>
                    <a href={data.invoice_summary_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-medium text-[#E53935] hover:underline">
                        <FileText className="w-4 h-4" /> View public invoice summary
                    </a>
                </section>
            )}

            <div className="text-center mt-12">
                <Link to="/ledger" className="inline-flex items-center gap-2 text-sm font-medium text-[#E53935] hover:underline">
                    <ArrowLeft className="w-4 h-4" /> Back to full ledger
                </Link>
            </div>
        </div>
    );
};

export default SpendDetail;
